import { API, api } from "@/lib/api";

export const PHOTO_MIME = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "image/gif"];
export const VIDEO_MIME = ["video/mp4", "video/quicktime", "video/webm"];
const EXT = { jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", heic: "image/heic", heif: "image/heif", gif: "image/gif", mp4: "video/mp4", mov: "video/quicktime", webm: "video/webm" };
export const mimeOf = (f) => (f.type && (PHOTO_MIME.includes(f.type) || VIDEO_MIME.includes(f.type)) ? f.type : EXT[(f.name.split(".").pop() || "").toLowerCase()] || f.type || "");
export const mediaUrl = (u) => (u ? `${API.replace(/\/api$/, "")}${u}` : u);
export const fmtDur = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.round((s || 0) % 60)).padStart(2, "0")}`;

// Poster frame + duration from a local video file.
export function videoInfo(file) {
  return new Promise((resolve) => {
    const v = document.createElement("video");
    const url = URL.createObjectURL(file);
    let done = false;
    const finish = (out) => { if (!done) { done = true; URL.revokeObjectURL(url); resolve(out); } };
    v.preload = "metadata"; v.muted = true; v.playsInline = true; v.src = url;
    v.onloadedmetadata = () => { v.currentTime = Math.min(1, (v.duration || 0) / 2); };
    v.onseeked = () => {
      const c = document.createElement("canvas");
      const scale = Math.min(1, 720 / (v.videoWidth || 720));
      c.width = Math.round((v.videoWidth || 640) * scale); c.height = Math.round((v.videoHeight || 360) * scale);
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      c.toBlob((b) => finish({ duration: v.duration, width: v.videoWidth, height: v.videoHeight, poster: b }), "image/jpeg", 0.8);
    };
    v.onerror = () => finish({ duration: null, poster: null });
    setTimeout(() => finish({ duration: v.duration || null, poster: null }), 8000);
  });
}

// Chunked upload: 4 MB parts, each retried up to 3 times. onProgress(0..1).
export async function uploadFile(base, convId, file, onProgress, extra = {}) {
  const mime = mimeOf(file);
  const { data: u } = await api.post(`${base}/uploads`, { conversation_id: convId, name: file.name || "file", mime, size: file.size, ...extra });
  for (let n = 0; n < u.parts; n += 1) {
    const part = file.slice(n * u.chunk_size, (n + 1) * u.chunk_size);
    let tries = 0;
    for (;;) {
      try {
        await api.put(`${base}/uploads/${u.id}/parts/${n}`, part, { headers: { "Content-Type": "application/octet-stream" },
          onUploadProgress: (e) => onProgress?.((n * u.chunk_size + (e.loaded || 0)) / file.size) });
        break;
      } catch (e) {
        tries += 1;
        if (tries >= 3 || e?.response?.status === 422 || e?.response?.status === 404) throw e;
        await new Promise((r) => setTimeout(r, 800 * tries));
      }
    }
  }
  await api.post(`${base}/uploads/${u.id}/complete`);
  onProgress?.(1);
  return u.id;
}

export async function uploadMedia(base, convId, file, onProgress) {
  const mime = mimeOf(file);
  if (!VIDEO_MIME.includes(mime)) return uploadFile(base, convId, file, onProgress);
  const info = await videoInfo(file);
  const id = await uploadFile(base, convId, file, onProgress, { duration: info.duration || null, width: info.width, height: info.height });
  if (info.poster) {
    const pf = new File([info.poster], "poster.jpg", { type: "image/jpeg" });
    const pid = await uploadFile(base, convId, pf, null, { poster_for: id });
    await api.post(`${base}/uploads/${id}/poster`, { poster_id: pid });
  }
  return id;
}

export const timeLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : d.toLocaleDateString([], { month: "short", day: "numeric" });
};
